/**
 * makeOffer.ts — starting a NEW offer from the web, against POST /api/offers.
 *
 * The make-offer half of the deal lane. dealCounter.ts and exchangeSend.ts both
 * wired the COUNTER and both say in their headers that starting a fresh offer
 * is out of scope and stays dead; this is the module that picks that up.
 * Until it existed, a signed-in visitor on gobazaar.co could not start an offer
 * at all: writeGuard stopped Blake's fabricator and nothing replaced it, so the
 * button did nothing and said nothing (card 1218886330579015).
 *
 * ── WHY IT IS A SEPARATE MODULE AND NOT A BRANCH IN dealCounter ──────────────
 *
 * 🪤 ONE SELECTOR, TWO JOBS. `[data-send-offer]` is both the composer's Send
 * Counter and the plain Make offer button on a listing (app.js:1061 renders one
 * button and switches its label on `counterFor`). dealCounter owns the counter
 * and returns SILENTLY when there is no counter in progress — that silence is
 * the seam this module installs into. The two are kept mutually exclusive in
 * the BRIDGE rather than here: `__gbzComposerCounter()` answers only with a
 * counter open, `__gbzComposerOffer()` only without one. If both ever answered,
 * one click would post two offers.
 *
 * ── THE FOUR THINGS THAT MAKE THIS HARDER THAN IT LOOKS ──────────────────────
 *
 * 🔴 1. THE ID SPACE, WHICH FAILS SILENTLY. A collection row carries `id` (a
 * user_collections key) and `lid` (the real listings.id). POST /api/offers wants
 * `lid`, and given anything else it does NOT error: offers.ts nulls the
 * unresolvable link and inserts the row anyway, so the offer saves, the cards
 * render from the name/image/value snapshot, and only the LINK is gone — the
 * link lockOfferItemsForTrade needs at accept time, without which the item stays
 * live and purchasable after being traded away. This module never sees `id`:
 * the translation happens once, in the bridge, where MY_COLLECTION is in scope.
 *
 * 🔴 2. THE BUYER ADDRESS SNAPSHOT. The app's two composers both send it
 * (new-offer.tsx:347, ThreadMakeOfferSheet.tsx:190) and this mirrors them.
 * ⚠️ But NOT for the reason the card gives: the "omitting it zeroed shipping and
 * tax" defect was closed on the SERVER, not only in the composers. Every money
 * path now resolves `offer.buyerShippingAddressJson ?? buyer profile address`
 * (offers.ts:148, :298, :2066, :2735, :4506) and two of them backfill the
 * column as they go (:2762, :4534). So omitting it today costs the SNAPSHOT,
 * not the money: the address is then resolved at payment time instead of offer
 * time, and a buyer who moved house between the two is charged and shipped
 * against the new one with nothing recording that it changed. Worth sending;
 * not worth REFUSING the offer over, which is why a failed read below proceeds.
 *
 * 🔴 3. THE OFFER TYPE IS DERIVED FROM THE PAYLOAD, NEVER FROM THE TAB. Same
 * rule dealCounter documents at length: the server cross-checks offerType three
 * ways and two of those refuse a body the tab name would have produced. Reused
 * verbatim through `offerTypeFor` rather than re-derived here.
 *
 * 🔴 4. HIS SUCCESS SENTENCE IS FALSE TWICE AND IS NOT REUSED. app.js:9757
 * toasts "Offer sent to @X · they have 48h to respond · escrow reserved".
 * There is no 48h window for a seller to answer a pending offer — the 48h is
 * the BUYER's pay deadline, anchored on accepted_at (dealDeadlines.ts:75,
 * scheduled at offers.ts:1229) — and nothing reserves escrow at offer time;
 * the insert touches no PaymentIntent. So this keeps the clause we can stand
 * behind and drops the two we cannot, the same convention dealActions.ts
 * follows for its three success lines. Any replacement wording is Blake's call
 * — writeGuard.ts:636-642, where the rule is written: what a control says is a
 * PRODUCT decision and inventing that copy here "would be the same class of
 * mistake as the toasts being suppressed".
 *   ⚠️ Not lines 604-614. Card 1218886330579015 cites those and they are the
 *   GUARDED_ENTER_INPUTS note about message composers, unrelated to copy.
 */

import { refreshDeals } from "./floorTransport";
import { offerTypeFor, type WireItem } from "./dealCounter";
import { SIGNIN_MESSAGE } from "./dealActions";

/** The composer's send — ALSO the Send Counter button, hence the counter gate. */
export const MAKE_OFFER_SELECTOR = "[data-send-offer]";
/* 🪤 THE MESSAGE BOX HAS NO ID. renderComposer (app.js:1054) emits a bare
   `<textarea placeholder="Message the seller · …">` inside #composer — unlike
   the counter sheet's `#counter-note`, which is why dealCounter can select one
   and this cannot. Scoped to #composer so it can never pick up the deal
   thread's message box, which is a different control on a different screen.
   Dropping it would post the offer and silently discard what the buyer typed. */
export const COMPOSER_NOTE_INPUT = "#composer textarea";

/** Only for a failure the server gave no sentence for. */
export const GENERIC_FAILURE_MESSAGE = "Couldn't send that offer — please try again";
/** Blake's own words for the same refusal (app.js: sendComposerCounter). */
export const NO_ITEMS_MESSAGE = "Pick at least one item — or switch to Cash";
/** The button carries no listing id, or it names a row the floor does not hold. */
export const UNRESOLVED_LISTING_MESSAGE =
  "We can't tell which listing this is — reopen it from the floor and try again";

/** Blake's sentence, minus the 48h and escrow clauses. See note 4 in the header. */
export function offerSentMessage(sellerHandle: string | null): string {
  return sellerHandle ? `Offer sent to @${sellerHandle}` : "Offer sent";
}

type MakeOfferWindow = Window & {
  toast?: (message: string) => void;
  __gbzCounterFor?: () => string | null;
  __gbzComposerOffer?: () => { tab: string; cash: number; items: WireItem[] } | null;
  __gbzListingRow?: (id: string) => { seller?: string } | null;
  __gbzOfferSent?: (listingId: string, sellerHandle: string | null) => void;
};

export interface NewOfferPayload {
  listingId: string;
  offerType: "cash" | "item" | "hybrid";
  cashAmount?: number;
  requestedCashAmount?: number;
  offeredItems?: WireItem[];
  requestedItems?: WireItem[];
  message?: string;
  buyerShippingAddressJson?: Record<string, unknown>;
  origin?: "sheet" | "exchange";
}

/**
 * The whole payload, as a pure function, so the rules can be measured without a
 * DOM — the same posture as buildCounterPayload and buildExchangePayload.
 *
 * `requestedItems` excludes the anchor: the offers route models `listingId` as
 * the primary listing and the requested array as extras BEYOND it, so including
 * it would ask for the same item twice and the feed would double it on the card.
 */
export function buildNewOfferPayload(input: {
  listingId: string;
  /** The buyer's side. */
  offeredItems: WireItem[];
  /** Extras asked of the seller, beyond the anchor listing. */
  requestedItems?: WireItem[];
  buyerCash: number;
  sellerCash?: number;
  message?: string;
  buyerShippingAddressJson?: Record<string, unknown> | null;
  origin: "sheet" | "exchange";
}): NewOfferPayload {
  const requestedItems = (input.requestedItems ?? []).filter(
    (it) => it.listingId !== input.listingId,
  );

  const payload: NewOfferPayload = {
    listingId: input.listingId,
    offerType: offerTypeFor(input.offeredItems, input.buyerCash),
    origin: input.origin,
  };
  if (input.offeredItems.length) payload.offeredItems = input.offeredItems;
  if (requestedItems.length) payload.requestedItems = requestedItems;
  /* 🔴 `> 0`, and it is the only thing keeping a negative off the wire. zod
     types cash_amount from PgReal, so its generated floor is gte(-8388608), and
     all three of the route's coherence gates test `> 0` as well — a negative
     amount passes every server check and lands in the row as real money pointed
     the wrong way. Omitting the field is also right for a plain zero: a cash leg
     of nothing is a leg that is not there. */
  if (input.buyerCash > 0) payload.cashAmount = input.buyerCash;
  if ((input.sellerCash ?? 0) > 0) payload.requestedCashAmount = input.sellerCash;
  if (input.message) payload.message = input.message;
  /* Sent when we have it, omitted when we do not — never `null`, which the zod
     schema would take as an explicit blank rather than an absent field. */
  if (input.buyerShippingAddressJson) {
    payload.buyerShippingAddressJson = input.buyerShippingAddressJson;
  }
  return payload;
}

/**
 * One per logical submit attempt, mirroring mobile's newAttemptId (lib/offers.ts:341)
 * so a transport-level duplicate dedups while a fresh attempt never replays a
 * stale response. crypto.randomUUID is present in every browser that reaches
 * this bundle; the fallback exists so a test environment without it still runs.
 */
export function newAttemptId(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  return `web-offer-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * The buyer's saved shipping address, for the snapshot. Best-effort BY DESIGN —
 * see note 2 in the header: the server resolves and backfills from the profile
 * when this is absent, so a failed read must not cost the visitor their offer.
 */
export async function readBuyerAddress(
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
): Promise<Record<string, unknown> | null> {
  try {
    const res = await authFetch("/api/users/me");
    if (!res.ok) return null;
    const body = (await res.json()) as { shippingAddressJson?: unknown };
    const addr = body.shippingAddressJson;
    if (addr && typeof addr === "object" && !Array.isArray(addr)) {
      return addr as Record<string, unknown>;
    }
    return null;
  } catch {
    return null;
  }
}

export function installMakeOffer(
  win: Window,
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
  myUserId: string,
  {
    notify = (message: string) => {
      if (!win.document.getElementById("toast")) return;
      (win as MakeOfferWindow).toast?.(message);
    },
  }: { notify?: (m: string) => void } = {},
): () => void {
  /* In flight, per listing. His handler was synchronous and needed no guard;
     with a real await behind the button a double-tap is two offers, and the
     second earns the route's one-pending-offer 409 rather than anything the
     visitor can read as a mistake of theirs. */
  const busy = new Set<string>();

  async function send(payload: NewOfferPayload, sellerHandle: string | null): Promise<void> {
    const listingId = payload.listingId;
    const res = await authFetch("/api/offers", {
      method: "POST",
      // authFetch sets neither, and the route parses JSON.
      headers: { "Content-Type": "application/json", "Idempotency-Key": newAttemptId() },
      body: JSON.stringify(payload),
    });

    if (res.status === 401) {
      notify(SIGNIN_MESSAGE);
      return;
    }
    if (!res.ok) {
      /* The server's own words when it gave any. The ones this path actually
         produces: "This listing does not accept that offer type.", "Cash can
         only ride one side of a trade", "You cannot make an offer on this
         listing" (the self-offer 403), the one-pending-offer 409, and the
         per-item "…is a cash-only item and can't be part of a trade."
         🪤 A zod rejection puts the first message on `errors[]` and the rest on
         `error`, and a 500 is an express HTML page with no JSON at all — so the
         parse must never be allowed to throw inside a capture listener. */
      const body = (await res.json().catch(() => ({}))) as {
        error?: string;
        errors?: Array<{ message?: string }>;
      };
      notify(body.errors?.[0]?.message ?? body.error ?? GENERIC_FAILURE_MESSAGE);
      return;
    }

    /* Then the truth, from the server. 201 returns the new offer row but not its
       items, and the words this surface renders come from the state machine
       anyway, so there is nothing repaintable in the response. Best-effort: the
       offer has already landed, so a failed re-read costs a repaint, not the offer. */
    await refreshDeals(win, authFetch, myUserId).catch(() => null);
    /* Confirmed: swap the composer for an in-place "Offer sent" state (app.js
       renderComposerSent) so the module does not look stuck and cannot be tapped
       again. Absent hook (older page) = the toast alone, as before. */
    (win as MakeOfferWindow).__gbzOfferSent?.(listingId, sellerHandle);
    notify(offerSentMessage(sellerHandle));
  }

  const onClick = (event: Event): void => {
    const target = event.target as Element | null;
    if (!target?.closest) return;
    const button = target.closest(MAKE_OFFER_SELECTOR);
    if (!button) return;

    const w = win as MakeOfferWindow;
    /* A counter is in progress, so this click belongs to dealCounter. Silence is
       correct: a refusal here would be this module claiming a control it does
       not own, and the visitor would read two toasts for one tap. */
    if (w.__gbzCounterFor?.()) return;

    const listingId = button.getAttribute("data-send-offer");
    if (!listingId) {
      notify(UNRESOLVED_LISTING_MESSAGE);
      return;
    }

    const composer = w.__gbzComposerOffer?.() ?? null;
    if (!composer) {
      notify(UNRESOLVED_LISTING_MESSAGE);
      return;
    }

    const items = composer.tab === "cash" ? [] : composer.items;
    if (composer.tab !== "cash" && items.length === 0) {
      notify(NO_ITEMS_MESSAGE);
      return;
    }

    /* His own number, not ours: `composerState.cash` is the number the box
       shows, the whole cash offer on the cash tab and the cash top-up on the
       hybrid tab (app.js: sendComposerCounter reads it the same way). The total
       is never stored, so nothing is subtracted here; recomputing the cash any
       other way would send a number the screen never showed (card
       1219184293238007, and Tyler's rule on card 1218887913215108: the request
       carries exactly what the box shows). The Exchange is a separate model with
       its own cash box and does not come through here. */
    const buyerCash = composer.tab === "items" ? 0 : Math.max(0, composer.cash);

    if (busy.has(listingId)) return;
    busy.add(listingId);

    const sellerHandle = w.__gbzListingRow?.(listingId)?.seller ?? null;
    const noteEl = win.document.querySelector(COMPOSER_NOTE_INPUT) as HTMLTextAreaElement | null;
    /* Read BEFORE the await: the address fetch gives the render loop a window
       in which patchComposer can replace #composer, and the replacement box is
       empty. Reading after would post an offer with the message dropped. */
    const message = noteEl?.value.trim() || undefined;

    void (async () => {
      const buyerShippingAddressJson = await readBuyerAddress(authFetch);
      const payload = buildNewOfferPayload({
        listingId,
        offeredItems: items,
        buyerCash,
        message,
        buyerShippingAddressJson,
        /* 'sheet' even for a multi-item selection: the server is authoritative
           and persists 'exchange' for multi-item offers regardless, which is the
           same thing mobile's new-offer.tsx sends and why. */
        origin: "sheet",
      });
      await send(payload, sellerHandle);
    })()
      .catch(() => notify(GENERIC_FAILURE_MESSAGE))
      .finally(() => busy.delete(listingId));
  };

  /* Capture on the document, after the guard, and deliberately not stopping
     propagation — the guard kills his fabricator and stays the fail-safe.
     `[data-send-offer]` stays in GUARDED_WRITES for the reason written there:
     if this module ever fails to install, the button falls back to DEAD rather
     than to his "escrow reserved" sentence over no server call. */
  win.document.addEventListener("click", onClick, true);
  return () => win.document.removeEventListener("click", onClick, true);
}
